<template>
  <div class="container mt-5 mb-4">
    <b-row class="justify-content-center">
      <b-col md="6">
        <div class="mb-3">
          <label for="email">{{ $t('app.patients.email') }}</label>
          <b-input-group>
            <input
              id="email"
              ref="email"
              v-model="email"
              v-validate="'required|email'"
              type="email"
              class="form-control"
              name="email"
              autofocus
              @keyup.delete="error = null"
            />
          </b-input-group>

          <b-form-invalid-feedback :state="!errors.has('email')">
            {{ errors.first('email') }}
          </b-form-invalid-feedback>
        </div>

        <div class="mb-3">
          <label for="password">Password</label>
          <b-input-group>
            <input
              id="password"
              v-model="password"
              v-validate="'required'"
              :type="isPasswordVisible ? 'text' : 'password'"
              class="form-control"
              name="password"
              @keyup.delete="error = null"
              @keydown.enter="onSubmit"
            />

            <template #append>
              <b-input-group-text
                style="cursor: pointer"
                @click="togglePasswordVisibility"
              >
                <b-icon :icon="isPasswordVisible ? 'eye-slash' : 'eye'" />
              </b-input-group-text>
            </template>
          </b-input-group>

          <b-form-invalid-feedback :state="!errors.has('password')">
            {{ errors.first('password') }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback :state="!error">
            {{ error }}
          </b-form-invalid-feedback>
        </div>

        <template v-if="showActivation">
          <label for="activationCode">{{
            $t('app.common.activationCode')
          }}</label>
          <b-input-group class="mb-3">
            <input
              id="activationCode"
              v-model="activationCode"
              v-validate="'required'"
              type="text"
              class="form-control"
              name="activation code"
              @keyup="activationError = null"
              @keydown.enter="onSubmit"
            />
            <b-form-invalid-feedback :state="!activationError">
              {{ activationError }}
            </b-form-invalid-feedback>
          </b-input-group>
        </template>

        <template v-if="showSerialNumber">
          <div class="mb-3">
            <div class="d-flex justify-content-between row px-3">
              <b-form-group
                class="mb-0 col-4 pl-0 pr-1"
                label-for="hardware-select"
                :label="$t('app.common.deviceType')"
                :invalid-feedback="errors.first('device type')"
              >
                <b-form-select
                  id="hardware-select"
                  v-model="selectedHardwareId"
                  v-validate="'required'"
                  name="device type"
                  :state="errors.has('device type') ? false : null"
                  @change="getSerialPrefix"
                >
                  <option
                    v-for="item in hardwareList"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </option>
                </b-form-select>
              </b-form-group>

              <b-form-group
                label-for="prefix"
                :invalid-feedback="getSerialError()"
                :label="$t('app.common.serialNumber')"
                class="serial-number-select col-8 mb-0 pr-0 pl-1"
              >
                <b-form-select
                  id="prefix-select"
                  v-model="serialPrefix"
                  v-validate="'required'"
                  name="prefix"
                  :disabled="!prefixes.length"
                  style="width: 30%"
                  :state="errors.has('prefix') ? false : null"
                >
                  <option
                    v-for="(prefix, i) in prefixes"
                    :key="i"
                    :value="prefix"
                  >
                    {{ prefix }}
                  </option>
                </b-form-select>
                <b-form-input
                  type="text"
                  id="serial-input"
                  v-model="serialNumber"
                  v-validate="'required|alpha_num|isSerial'"
                  name="serial"
                  @keyup="serialNumberError = null"
                  :state="errors.has('serial') ? false : null"
                  style="width: 70%"
                />
                <b-form-invalid-feedback :state="!serialNumberError">
                  {{ serialNumberError }}
                </b-form-invalid-feedback>
              </b-form-group>
            </div>
          </div>
        </template>

        <template v-if="showDistributors">
          <b-form-group
            label-for="distributor-select"
            :label="$t('app.common.loginDistributor')"
          >
            <b-form-select
              id="distributor-select"
              v-model="selectedDistributorId"
              v-validate="'required'"
              name="distributor type"
              :state="errors.has('distributor type') ? false : null"
              @change="distributorError = null"
            >
              <option
                v-for="item in distributorList"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </option>
            </b-form-select>
            <b-form-invalid-feedback :state="!distributorError">
              {{ distributorError }}
            </b-form-invalid-feedback>
          </b-form-group>
        </template>

        <b-button
          block
          variant="primary"
          class="px-4 mt-4"
          id="login-button"
          @click="onSubmit"
        >
          {{ $t('app.common.login') }}
        </b-button>
        <b-button
          v-if="!isShared"
          id="activate-license-button"
          block
          variant="outline-secondary"
          class="px-4 mt-3"
          :to="
            localePath({
              name: 'signup'
            })
          "
        >
          {{ $t('pages.signup.activateLicense') }}
        </b-button>
        <div v-if="!isShared" class="mt-3 text-center">
          <nuxt-link
            :to="
              localePath({
                name: 'forgot'
              })
            "
          >
            {{ $t('app.common.forgotPassword') }}
          </nuxt-link>
        </div>

        <div v-if="isShared" class="mt-3 text-center">
          <nuxt-link
            :to="
              localePath({
                name: 'forgot-password'
              })
            "
          >
            {{ $t('app.common.forgotPassword') }}
          </nuxt-link>
        </div>
        <div
          v-if="!isShared && softwareVersion"
          class="mt-4 text-center"
          style="color: #6c757d;"
        >
          Software ver. {{ softwareVersion }}
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Validator } from 'vee-validate'
import validationRules from '~/components/utils/validationRules'

export default {
  layout: 'login',
  head() {
    return {
      title: 'Login'
    }
  },
  data() {
    return {
      showDistributors: false,
      selectedDistributorId: null,
      distributorList: [],
      email: '',
      password: '',
      activationCode: '',
      error: null,
      prefixes: [],
      activationError: null,
      serialNumberError: null,
      distributorError: null,
      showActivation: false,
      showSerialNumber: false,
      hardwareList: [],
      selectedHardwareId: null,
      serialNumber: null,
      serialPrefix: null,
      isPasswordVisible: false
    }
  },
  computed: {
    ...mapGetters({
      softwareVersion: 'user/softwareVersion',
      isShared: 'utils/isShared'
    })
  },
  async asyncData({ $axios, store }) {
    try {
      if (process.env.platform === 'electron') {
        await $axios.post(
          `${store.getters['network/localAPI']}/check-path-to-db`
        )
      }
    } catch (error) {
      console.error(error)
    }
  },
  mounted() {
    this.$refs.email.focus()
  },
  created() {
    Validator.extend('isSerial', {
      getMessage: () => this.$t('app.common.invalid.serial'),
      validate: value => validationRules.validateSerial(value)
    })
    Validator.extend('alpha_num', {
      ...Validator.rules.alpha_num,
      getMessage: () => this.$t('app.common.invalid.alpha_num')
    })
    Validator.extend('required', {
      ...Validator.rules.required,
      getMessage: () => this.$t('app.forms.requiredField')
    })
  },
  methods: {
    ...mapActions({
      login: 'user/login',
      getApplications: 'applications/getApplications'
    }),
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible
    },
    getSerialError() {
      if (this.errors.has('prefix')) {
        return this.$t('app.forms.requiredPrefix')
      }

      if (this.errors.has('activation code')) {
        this.activationError = this.$t('app.common.activationError')
      }

      if (this.errors.has('serial')) {
        const serialError = this.errors.items.find(it => it.field === 'serial')
        return serialError && serialError.rule !== 'required'
          ? this.errors.first('serial')
          : this.serialNumberError
          ? ''
          : this.$t('app.forms.requiredField')
      }

      return this.$t('app.forms.requiredField')
    },
    getSerialPrefix(value) {
      this.prefixes = []
      const hardware = this.allHardware.find(item => {
        return item.id === value
      })?.name
      this.prefixes = this.allHardware
        .filter(item => item.name === hardware)
        ?.map(item => item.code)
    },
    getHardwareList() {
      if (this.hardwareList.length) return
      this.loading = true
      this.$axios
        .post('/akern-platform-backend/v1/common/hardware/list', {})
        .then(response => {
          this.allHardware = response.data.hardware
          this.allHardware.reduce((accu, curr) => {
            if (!accu?.find(item => curr.name === item.name)) accu.push(curr)
            return accu
          }, this.hardwareList)
        })
    },
    checkTable() {
      this.loading = true
      this.selectedDistributorId = null
      this.$axios
        .post(
          '/akern-platform-backend/v1/distributor/distributors/checkList-by-email',
          {
            email: this.email
          }
        )
        .then(response => {
          this.distributorList = response.data.distributors
        })
    },
    resetErrors() {
      this.error = null
      this.activationError = null
      this.serialNumberError = null
      this.distributorError = null
    },
    onSubmit() {
      this.$validator.validateAll().then(valid => {
        if (valid) {
          const data = {
            email: this.email,
            password: this.password
          }
          if (this.activationCode) {
            data.activationCode = this.activationCode
          }
          if (
            this.serialNumber &&
            this.serialPrefix &&
            this.selectedHardwareId
          ) {
            data.devices = [
              {
                serialNumber: this.serialPrefix + this.serialNumber,
                hardwareId: this.selectedHardwareId
              }
            ]
          }
          if (this.selectedDistributorId) {
            data.distributor = this.selectedDistributorId
          }
          this.login(data)
            .then(() => {
              this.getApplications()
                .then(_ => {
                  this.$router.push(
                    this.localePath({
                      name: 'index'
                    })
                  )
                })
                .catch(err => {
                  this.error =
                    err.response.data.message ||
                    err.response.data.exception.message
                })
            })
            .catch(err => {
              if (this.isShared) {
                this.error = err.response.data.message
                return
              }
              const errorList = err.response.data.exception.message.split(', ')
              this.activationError = err.response.data.exception.data
                ?.activationCode
                ? err.response.data.exception.data?.activationCode[0]
                : null
              this.serialNumberError = err.response.data.exception.data
                ?.serialNumber
                ? err.response.data.exception.data?.serialNumber[0]
                : null
              this.distributorError = err.response.data.exception.data
                ?.distributor
                ? err.response.data.exception.data?.distributor[0]
                : null
              if (errorList.includes('activationCode')) {
                this.activationError = this.$t('app.common.activationError')
                this.showActivation = true
              }
              if (errorList.includes('Serial Number already taken')) {
                this.serialNumberError = this.$t('app.common.serialTaken')
                this.showSerialNumber = true
                this.getHardwareList()
              }
              if (errorList.includes('serialNumber')) {
                this.serialNumberError = this.$t('app.common.serialError')
                this.showSerialNumber = true
                this.getHardwareList()
              }
              if (errorList.includes('distributor')) {
                this.distributorError = this.$t('app.common.distributorError')
                this.showDistributors = true
                this.checkTable()
              }
              if (!this.showActivation && !this.showSerialNumber) {
                this.error =
                  err.response.data.message ||
                  err.response.data.exception.message
              }
            })
        }
      })
    }
  }
}
</script>
<style>
.serial-number-select > div {
  display: flex;
  flex-wrap: wrap;
}
</style>
